import type { ParsedUrlQuery } from "querystring";
import type {
  GetServerSidePropsContext,
  GetStaticPaths,
  InferGetServerSidePropsType,
  PreviewData,
} from "next";
import Image from "next/image";
import Link from "next/link";
import { NotionRenderer } from "react-notion-x";

import { NotionService } from "@acme/api/services";

import "react-notion-x/src/styles.css";

import { faChevronLeft } from "@fortawesome/pro-regular-svg-icons";
import classNames from "classnames";

import PageLayout from "~/components/layout/PageLayout";
import { useTheme } from "~/components/theme/ThemeProvider";
import Button from "~/components/ui-elements/Button";
import { getCoverImage } from "../blog";

type PageProps = InferGetServerSidePropsType<typeof getStaticProps>;

const Post = ({ blog }: PageProps) => {
  const { theme } = useTheme();

  if (!blog) return null;

  const properties = blog.properties;
  const pageContent = blog.pageContent;
  const coverImage = getCoverImage(properties.title);

  const date = properties.publishDate
    ? new Date(properties.publishDate).toISOString()
    : undefined;

  // Only use the cover image if it's not the default image
  const seoImage =
    coverImage === "/images/blog/vital-gradient.png" ? undefined : coverImage;

  return (
    <PageLayout
      seo={{
        title: properties.title,
        description: properties.description,
        image: seoImage,
        publishedTime: date,
        modifiedTime: date,
      }}
    >
      <div className="flex min-h-screen w-full flex-col items-center justify-start">
        <div className="relative flex h-64 w-full">
          <Image
            src={getCoverImage(properties.title)}
            alt={properties.title}
            className="object-cover object-bottom"
            priority
            fill
          />
        </div>
        <Link href={"/blog"} className="top-64 m-4 self-start">
          <Button variant={"ghost"} leftIcon={faChevronLeft}>
            Blog
          </Button>
        </Link>
        <div className="w-full max-w-[708px] px-4 pt-24">
          <h1 className="mb-3 text-5xl font-semibold opacity-80">
            {properties.title}
          </h1>
          <h4 className="text-lg font-light">{properties.description}</h4>
        </div>
        {/* NotionRenderer uses "dark-mode" as the name of the class for dark mode */}
        <div
          className={classNames(
            "mb-64 w-screen",
            theme === "dark" ? "dark-mode" : "",
          )}
        >
          <NotionRenderer
            recordMap={pageContent}
            components={{
              Collection: () => null, // implement this to support notion databases
              nextImage: Image,
              nextLink: Link,
            }}
          />
        </div>
      </div>
    </PageLayout>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const blogs = await NotionService.getBlogPosts();
  const paths = blogs.map(({ id }) => ({ params: { id: id } }));

  return { paths, fallback: "blocking" };
};

export const getStaticProps = async (
  context: GetServerSidePropsContext<ParsedUrlQuery, PreviewData>,
) => {
  const id = context.params?.id as string;
  const blog = await NotionService.getBlogById(id);
  return { props: { blog } };
};

export default Post;
